import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent, cn, Skeleton, Spinner, Switch } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { Database } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { StatusChip } from '@/components/ui/status-chip';
import { READ_REPLICAS_QUERY_KEY, useReadReplicas } from '@/hooks/queries/devops/use-read-replicas';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage } from '@/lib/api/error';
import { config } from '@/lib/env';
import { putHealthyIps, type ReadReplica, type ReadReplicaState } from '@/services/api/devops';

const SKELETON_ROW_IDS = ['skeleton-row-1', 'skeleton-row-2', 'skeleton-row-3'];

// The wire `healthyIps` field is ignored on purpose — healthy IPs are always
// derived from `replicas[].healthy` so the switches are the single source of truth.
const deriveHealthyIps = (replicas: ReadReplica[]): string[] => replicas.filter((replica) => replica.healthy).map((replica) => replica.ip);

const sortedJoin = (ips: string[]): string => [...ips].sort().join(',');

/** Local editable mirror of the server state (wire `healthyIps` dropped). */
type ReplicaViewState = Pick<ReadReplicaState, 'replicas' | 'orphanedHealthyIps' | 'canEdit'>;

interface ReplicaRowProps {
  replica: ReadReplica;
  isDisabled: boolean;
  onToggle: (ip: string) => void;
}

const ReplicaRow = ({ replica, isDisabled, onToggle }: ReplicaRowProps) => (
  <li className="flex items-center gap-4 px-4 py-3">
    <Database aria-hidden className={cn('size-5 shrink-0', replica.healthy ? 'text-emerald-500' : 'text-muted-foreground')} />
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate font-medium">{replica.name ?? 'Unnamed node'}</span>
      <span className="truncate text-sm text-muted-foreground">{replica.ip}</span>
    </div>
    <StatusChip variant={replica.healthy ? 'success' : 'muted'}>{replica.healthy ? 'In load' : 'Out of load'}</StatusChip>
    <Switch aria-label={`Toggle traffic for ${replica.name ?? replica.ip}`} checked={replica.healthy} disabled={isDisabled} onCheckedChange={() => onToggle(replica.ip)} />
  </li>
);
ReplicaRow.displayName = 'ReplicaRow';

const ReplicaListSkeleton = () => (
  <Card className="max-w-2xl">
    <CardContent className="flex flex-col gap-4 p-4">
      {SKELETON_ROW_IDS.map((id) => (
        <div className="flex items-center gap-4" key={id}>
          <Skeleton className="size-5 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3.5 w-28" />
          </div>
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-5 w-9 rounded-full" />
        </div>
      ))}
    </CardContent>
  </Card>
);
ReplicaListSkeleton.displayName = 'ReplicaListSkeleton';

const QuestDbPage = () => {
  const queryClient = useQueryClient();
  const readReplicasQuery = useReadReplicas();
  const { data, isPending, isError, error, refetch } = readReplicasQuery;

  const [viewState, setViewState] = useState<ReplicaViewState | null>(null);
  const [savedHealthyKey, setSavedHealthyKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const applyState = useCallback((state: ReadReplicaState) => {
    const { replicas, orphanedHealthyIps, canEdit } = state;
    setViewState({ replicas, orphanedHealthyIps, canEdit });
    setSavedHealthyKey(sortedJoin(deriveHealthyIps(replicas)));
  }, []);

  // Re-seed the editable state on every load (structural sharing means an
  // unchanged server payload keeps the same reference and does not re-seed).
  useEffect(() => {
    if (data) applyState(data);
  }, [data, applyState]);

  useQueryErrorToast(readReplicasQuery, 'Failed to load QuestDB read replicas');

  const replicas = viewState?.replicas ?? [];
  const orphanedHealthyIps = viewState?.orphanedHealthyIps ?? [];
  const canEdit = viewState?.canEdit ?? false;
  const healthyIps = deriveHealthyIps(replicas);
  const healthyCount = healthyIps.length;
  // Toggling a node off and back on again returns to a clean state.
  const isDirty = viewState !== null && sortedJoin(healthyIps) !== savedHealthyKey;

  const toggleReplica = (ip: string) =>
    setViewState((previous) =>
      previous
        ? {
            ...previous,
            replicas: previous.replicas.map((replica) => (replica.ip === ip ? { ...replica, healthy: !replica.healthy } : replica)),
          }
        : previous,
    );

  const handleReset = async () => {
    const result = await refetch();
    // Explicit re-seed: a refetch returning byte-identical data keeps the same
    // reference, so the seeding effect alone would not discard local edits.
    if (result.data) applyState(result.data);
  };

  const handleSave = () => {
    void confirmAction({
      title: 'Confirm traffic change',
      requireJustification: false,
      destructive: healthyCount === 0,
      actionLabel: 'Confirm',
      description:
        (healthyCount === 0 ? 'You are about to take ALL nodes out of load. No replica will receive query traffic. ' : '') +
        `Set ${healthyCount} of ${replicas.length} node(s) to receive traffic?`,
      // A throw (e.g. the 400 for unknown IPs) keeps the dialog open showing the API error.
      onConfirm: async () => {
        setIsSaving(true);
        try {
          const state = await putHealthyIps(healthyIps);
          applyState(state);
          queryClient.setQueryData(READ_REPLICAS_QUERY_KEY, state);
          toast.success('QuestDB read replica traffic updated');
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  const isSwitchDisabled = !canEdit || isSaving;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-semibold">QuestDB Read Replicas</h1>
          <p className="text-muted-foreground">Choose which read-replica nodes should receive query traffic. Deselected nodes are pulled out of load.</p>
        </div>
        <Badge variant="outline">{config.APP_ENV}</Badge>
      </header>

      {isError && !viewState ? <QueryErrorBanner message={`Failed to load: ${getErrorMessage(error)}`} onRetry={() => refetch()} /> : null}

      {isPending ? <ReplicaListSkeleton /> : null}

      {viewState ? (
        <section aria-label="QuestDB read replica traffic" className="flex max-w-2xl flex-col gap-4">
          {replicas.length > 0 && healthyCount === 0 ? (
            <Alert variant="destructive">
              <AlertTitle>No nodes selected.</AlertTitle>
              <AlertDescription>
                {isDirty ? 'Saving this selection means no' : 'No'} QuestDB read replica will receive traffic and API requests that depend on QuestDB will fail.
              </AlertDescription>
            </Alert>
          ) : null}

          {!canEdit ? (
            <Alert>
              <AlertTitle>You have view-only access.</AlertTitle>
              <AlertDescription>You are not authorised to change QuestDB traffic routing.</AlertDescription>
            </Alert>
          ) : null}

          {orphanedHealthyIps.length > 0 ? (
            <Alert className="border-amber-500/40 bg-amber-500/10 text-amber-500">
              <AlertDescription>
                {orphanedHealthyIps.length} healthy IP(s) in Redis are no longer in the available replica set: {orphanedHealthyIps.join(', ')}. Saving your selection will remove
                them.
              </AlertDescription>
            </Alert>
          ) : null}

          {replicas.length === 0 ? (
            <Alert>
              <AlertDescription>No read replicas are currently available.</AlertDescription>
            </Alert>
          ) : null}

          {replicas.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y">
                  {replicas.map((replica) => (
                    <ReplicaRow isDisabled={isSwitchDisabled} key={replica.ip} onToggle={toggleReplica} replica={replica} />
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          <footer className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              {healthyCount} of {replicas.length} node(s) receiving traffic
            </span>
            <div className="flex items-center gap-2">
              <Button disabled={!isDirty || isSaving} onClick={handleReset} variant="outline">
                Reset
              </Button>
              <Button className="gap-2" disabled={!canEdit || !isDirty || isSaving} onClick={handleSave} variant="primary">
                {isSaving ? <Spinner className="size-4" /> : null}
                Save changes
              </Button>
            </div>
          </footer>
        </section>
      ) : null}
    </div>
  );
};
QuestDbPage.displayName = 'QuestDbPage';

export default QuestDbPage;
